import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
    andAllWhere,
    andWhere,
    buildQuery,
    CombinedFilter,
    FilterFn,
    noop,
    Ops,
    orderBy,
    OrderVariant,
    orWhere,
    page,
    select,
    where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import { AbstractService } from "app/services/abstract.service";
import { environment as env } from "../../../../../../environments/environment.prod";
import { MaterialStockAttributes, OrderAttributes, OrderDetailAttributes } from "@albatrosdeveloper/ave-models-package";
import { IPagination, IPaginationResult } from "app/common/interfaces";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";
import { lossInfo, lossResponse } from "../../loss-report/dto/loss-report.dto";
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';

const apiUrl = env.API_AVE_ORDER_URL;
const apiReportUrl = env.API_AVE_ORDER_REPORT_URL;
const moduleName = "order_report";

@Injectable({
    providedIn: "root",
})
export class OrderCustomerService extends AbstractService<OrderDetailAttributes, OrderDetailAttributes, OrderDetailAttributes> {
    /** The last filter used to load the list */
    currentFilter: CombinedFilter<OrderDetailAttributes>;
    currentFilterQuery: any;

    constructor(
        http: HttpClient,
    ) {
        super(apiUrl, moduleName, http);
    }

    /**
     * 📍 Used in:
     * - Reportes/Ventas/Solicitudes/
     *          de Devoluciones ('return-product-request'), de Vendedores ('order-seller')
     * - Reportes/Ventas/Listados/
     *          Pedidos ('order-customer'), Carga In/Out ('charge-in-out'), Entrega ('delivery')
     *          Entregas rechazadas ('delivery-rejection'), Facturas ('order-invoice'), Cambios('change-product')
     *          Devoluciones ('return-product'), Mermas('loss-list')
     */
    findByFilter(filter: CombinedFilter<OrderDetailAttributes>, options?: RequestOptions) {
        const API_URL: string =
            apiReportUrl +
            `api/${moduleName}/withCount`;
        const headers: Record<string, any> = {};
        // see order-customer-export for more info about this header
        // ctx: stands for context
        if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
        //return this.http.get<IPaginationResult<OrderDetailAttributes>>(API_URL, { headers });
        return this.http.post<IPaginationResult<OrderDetailAttributes>>(API_URL, 
            { filter: filter }, 
            { headers });
    }

    /**
     * Pagination
     * @param options Pagination options
     * @returns Paginated data
     * 📍 Used in:
     * - Reportes/Ventas/Solicitudes
     *          de Cambios (`request-change`) , de Devoluciones ('eturn-product-request'), de Vendedores('order-seller')
     * - Reportes/Ventas/Listados/
     *          Pedidos ('order-customer'), Carga In/Out ('charge-in-out'), Entrega ('delivery')
     *          Entregas rechazadas ('delivery-rejection'), Facturas ('order-invoice'), Cambios('change-product')
     *          Devoluciones ('return-product'), Mermas ('loss-list')
     */
    findAllWithCount(
        options: IPagination<OrderDetailAttributes>,
        dataToFilter: IFilters,
    ): Observable<IPaginationResult<OrderDetailAttributes>> {
        const hasSearch = !!options.search;

        /**
         * Construye las condiciones base que SIEMPRE deben aplicarse.
         * Incluye: _deleted=false, y todos los filtros condicionales.
         *
         * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
         */
        const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<OrderDetailAttributes>[] => {
            const deletedFilter: FilterFn<OrderDetailAttributes> = params.useOrWhere
                ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
                : where('_deleted', Ops.eq(false));   // Primer objeto del array

            const filters: FilterFn<OrderDetailAttributes>[] = [deletedFilter];

            // Filtro de tipo de documento
            if (dataToFilter.type) {
                filters.push(andWhere("type.code" as keyof OrderDetailAttributes, Ops.eq(dataToFilter.type)));
            }

            // Filtro de clase de documento
            if (dataToFilter.documentClass && dataToFilter.documentClass.length > 0) {
                filters.push(andWhere("class.code" as keyof OrderDetailAttributes, Ops.in(...dataToFilter.documentClass)));
            }

            // Filtro de vendedor
            if (dataToFilter.seller && dataToFilter.seller.length > 0) {
                filters.push(andWhere("user.user_login.username" as keyof OrderDetailAttributes, Ops.in(...dataToFilter.seller)));
            }

            // Filtro de viaje (con viaje / sin viaje)
            if (dataToFilter.withTravel == '1') {
                filters.push(andWhere('trip' as keyof OrderDetailAttributes, Ops.not(null)));
            } else if (dataToFilter.withTravel == '2') {
                filters.push(andWhere('trip' as keyof OrderDetailAttributes, null));
            }

            // Filtro de estado ERP
            if (dataToFilter.statusERP && dataToFilter.statusERP.length > 0 && !dataToFilter.statusERP.includes(null)) {
                filters.push(andWhere('statusERP', Ops.in(...dataToFilter.statusERP)));
            }

            // Filtro de estado
            if (dataToFilter.status && dataToFilter.status.length > 0 && !dataToFilter.status.includes(null)) {
                filters.push(andWhere('statusId', Ops.in(...dataToFilter.status)));
            }

            // Filtro de tipo de cliente
            if (dataToFilter.typeCustomer && dataToFilter.typeCustomer.length > 0 && !dataToFilter.typeCustomer.includes(null)) {
                filters.push(andWhere('shipTo.types.code' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.typeCustomer)));
            }

            // Filtro de grupo de material
            if (dataToFilter.materialGroupId && dataToFilter.materialGroupId.length > 0 && !dataToFilter.materialGroupId.includes(null)) {
                filters.push(andWhere('detail.material.materialGroups.code' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.materialGroupId)));
            }

            // Filtro de soldTo
            if (dataToFilter.soldTo && dataToFilter.soldTo.length > 0) {
                filters.push(andWhere('soldTo.id' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.soldTo)));
            }

            // Filtro de shipTo
            if (dataToFilter.shipTo && dataToFilter.shipTo.length > 0) {
                filters.push(andWhere('shipTo.id' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.shipTo)));
            }

            // Filtro de oficina de ventas
            if (dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0) {
                filters.push(andWhere('salesOffice.id' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.salesOfficeId)));
            }

            // Filtro de almacén (sin duplicación)
            if (dataToFilter.warehouseId && dataToFilter.warehouseId.length > 0) {
                filters.push(andWhere('warehouses.id' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.warehouseId)));
            }

            // Filtro de subruta (sin duplicación)
            if (dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0) {
                filters.push(andWhere('subRoute.id' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.subRouteId)));
            }

            // Filtro de material (sin duplicación)
            if (dataToFilter.materialId && dataToFilter.materialId.length > 0) {
                filters.push(andWhere('detail.material.code' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.materialId)));
            }

            // Filtro de fecha de entrega
            if (dataToFilter.startDeliveryDate && dataToFilter.endDeliveryDate) {
                filters.push(andWhere("deliveryDateTime" as keyof OrderDetailAttributes, Ops.btwnDates(dataToFilter.startDeliveryDate, dataToFilter.endDeliveryDate)));
            }

            // Filtro de fecha de carga
            if (dataToFilter.pickerUpload_start && dataToFilter.pickerUpload_end) {
                filters.push(andWhere("loadDateTime" as keyof OrderDetailAttributes,  Ops.btwnDates(dataToFilter.pickerUpload_start, dataToFilter.pickerUpload_end)));
            }

            // Filtro de venta directa
            if (dataToFilter.isDirectSale != null) {
                filters.push(andWhere("isDirectSale" as keyof OrderDetailAttributes, Ops.eq(dataToFilter.isDirectSale)));
            }

            // Filtro de tipo de devolución
            if (dataToFilter.returnType != null) {
                filters.push(andWhere(<any>"detail.returnType.code", Ops.in(...dataToFilter.returnType)));
            }

            // Filtro de fecha de creación
            if (dataToFilter.startDate && dataToFilter.endDate) {
                filters.push(andWhere("createdAt", Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate)));
            }

            // Filtro de inicio de viaje
            if (dataToFilter.startTrip && dataToFilter.endTrip) {
                filters.push(andWhere("trip.dateHourStart" as keyof OrderDetailAttributes, Ops.btwnDates(dataToFilter.startTrip, dataToFilter.endTrip)));
            }

            // Filtro de fin de viaje
            if (dataToFilter.end_startTrip && dataToFilter.end_endTrip) {
                filters.push(andWhere("trip.dateHourEnd" as keyof OrderDetailAttributes, Ops.btwnDates(dataToFilter.end_startTrip, dataToFilter.end_endTrip)));
            }

            // Filtro especial: ordersToFilter (solo cuando NO hay búsqueda)
            if (dataToFilter.ordersToFilter && dataToFilter.ordersToFilter.length > 0 ) {
                filters.push(andWhere("detail.material.code" as keyof OrderDetailAttributes, Ops.in(...dataToFilter.ordersToFilter)));
            }

            return filters;
        };

        // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

        // Objeto 1: Condiciones base + búsqueda por SERIAL (o solo condiciones si no hay búsqueda)
        const searchBySerialQueryObject: FilterFn<OrderDetailAttributes>[] = [
            ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
            hasSearch ? andWhere(<any>"serial", Ops.like(options.search!, "i")) : noop()
        ];

        // Objeto 2: Condiciones base + búsqueda por MATERIAL NAME (solo si hay búsqueda)
        const searchByMaterialNameQueryObject: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
            andWhere(<any>"detail.material.name", Ops.like(options.search!, "i"))
        ] : [];

        // Objeto 3: Condiciones base + búsqueda por MATERIAL CODE (solo si hay búsqueda)
        const searchByMaterialCodeQueryObject: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(<any>"detail.material.code", Ops.like(options.search!, "i"))
        ] : [];

        // Objeto 4: Condiciones base + búsqueda por SHIPTO EXTERNAL CODE (solo si hay búsqueda)
        const searchByShipToCodeQueryObject: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(<any>"shipTo.externalKeys.code", Ops.like(options.search!, "i"))
        ] : [];

        // Objeto 5: Condiciones base + búsqueda por SHIPTO NAME (solo si hay búsqueda)
        const searchByShipToNameQueryObject: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(<any>"shipTo.name", Ops.like(options.search!, "i"))
        ] : [];

        // Objeto 6: Condiciones base + búsqueda por SUBROUTE NAME (solo si hay búsqueda)
        const searchBySubRouteNameQueryObject: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(<any>"subRoute.name", Ops.like(options.search!, "i"))
        ] : [];

        // Objeto 7: Condiciones base + búsqueda por USERNAME (solo si hay búsqueda)
        const searchByUsernameQueryObject: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere(<any>"user.user_login.username", Ops.like(options.search!, "i"))
        ] : [];

        // ========== ENSAMBLAJE FINAL ==========

        const query = buildQuery<OrderDetailAttributes>(
            // Expandimos los siete objetos en el buildQuery
            ...searchBySerialQueryObject,
            ...searchByMaterialNameQueryObject,
            ...searchByMaterialCodeQueryObject,
            ...searchByShipToCodeQueryObject,
            ...searchByShipToNameQueryObject,
            ...searchBySubRouteNameQueryObject,
            ...searchByUsernameQueryObject,

            // Ordenamiento y paginación
            orderBy(
                options.sort ?? "_id",
                options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            ),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        this.currentFilter = query;

        const API_URL: string = apiReportUrl + `api/${moduleName}/withCount`;
        return this.http.post<IPaginationResult<OrderDetailAttributes>>(API_URL, {
            filter: query
        });
    }

    findAllWithCountShrinkages(
        options: IPagination<lossInfo>,
        dataToFilter: IFilters,
    ): Observable<lossResponse> {
        const query = buildQuery<lossInfo>(
            where('_deleted', Ops.eq(false)),
            andWhere("detail.material.name" as keyof lossInfo, Ops.like(options.search ?? "", "i")),
            orWhere('detail.material.code' as keyof lossInfo, Ops.like(options.search ?? "", "i")),
            orWhere('shipTo.externalKeys.code' as keyof lossInfo, Ops.like(options.search ?? "", "i")),
            orWhere('shipTo.name' as keyof lossInfo, Ops.like(options.search ?? "", "i")),
            orWhere("subRoute.name" as keyof lossInfo, Ops.like(options.search ?? "", "i")),
            orWhere("user.user_login.username" as keyof lossInfo, Ops.like(options.search ?? "", "i")),
            orWhere("serial", Ops.like(options.search ?? "", "i")),

            dataToFilter.startDate && dataToFilter.endDate
                ? andAllWhere("createdAt", Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate))
                : noop(),
            dataToFilter.startTrip && dataToFilter.endTrip
                 ? andAllWhere("trip.dateHourStartTeoric" as keyof lossInfo, Ops.btwnDates(dataToFilter.startTrip, dataToFilter.endTrip))
                : noop(),
            dataToFilter.end_startTrip && dataToFilter.end_endTrip
                ? andAllWhere("trip.dateHourEndTeoric" as keyof lossInfo, Ops.btwnDates(dataToFilter.end_startTrip, dataToFilter.end_endTrip))
                : noop(),

            dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0 ?
                andAllWhere('subRoute.id' as keyof lossInfo, Ops.in(...dataToFilter.subRouteId))
            : noop(),

            dataToFilter?.seller && dataToFilter?.seller?.length > 0 ?
                andAllWhere('user.user_login.username' as keyof lossInfo, Ops.in(...dataToFilter.seller))
            : noop(),

            dataToFilter?.customerId && dataToFilter?.customerId?.length > 0 ?
                andAllWhere('soldTo.id' as keyof lossInfo, Ops.in(...dataToFilter.customerId))
            : noop(),

            dataToFilter.materialGroupId && dataToFilter.materialGroupId.length > 0 ?
                andAllWhere('detail.material.materialGroups.code' as keyof lossInfo, Ops.in(...dataToFilter.materialGroupId))
            : noop(),

            dataToFilter.materialId && dataToFilter.materialId.length > 0 ?
                andAllWhere('detail.material.code' as keyof lossInfo, Ops.in(...dataToFilter.materialId)) 
            : noop(),

            dataToFilter.typeCustomer && dataToFilter.typeCustomer.length > 0 ?
                andAllWhere('soldTo.types.code' as keyof lossInfo, Ops.in(...dataToFilter.typeCustomer))
            : noop(),

            dataToFilter.status && dataToFilter.status.length > 0 ?
                andAllWhere('detail.statusId' as keyof lossInfo, Ops.in(...dataToFilter.status))
            : noop(),

            dataToFilter.statusERP && dataToFilter.statusERP.length > 0 ?
                andAllWhere('detail.statusERP' as keyof lossInfo, Ops.in(...dataToFilter.status))
            : noop(),

            dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0 ?
                andAllWhere('salesOffice.id' as keyof lossInfo, Ops.in(...dataToFilter.salesOfficeId))
            : noop(),

            dataToFilter?.companyId?
                andAllWhere('company.id' as keyof lossInfo, Ops.eq(dataToFilter.companyId))
            : noop(),

            orderBy(
                options.sort ?? "createdAt",
                options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
            ),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        const API_URL: string = apiReportUrl + `api/${moduleName}/shrinkages`;
        return this.http.post<lossResponse>(API_URL, {
            filter: query
        });
    }

    findAllDownloadWithCount(
        options: IPagination<OrderDetailAttributes>,
        deliveryDateTime: Date,
        salesOfficeId: string,
        routes: string[],
    ): Observable<any> {

        const query = buildQuery<OrderDetailAttributes>(
            where('_deleted', Ops.eq(false)),
            //andAllWhere('salesOffice._id' as keyof OrderDetailAttributes, Ops.in(...[salesOfficeId])),
            routes && routes.length > 0 ? andAllWhere('subRoute.id' as keyof OrderDetailAttributes, Ops.in(...routes)) : noop(),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        this.currentFilterQuery = query;

        const API_URL: string =
            apiUrl +
            `api/${moduleName}/product_change?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<any>(API_URL);
    }

/**
 * Pagination
 * @param options Pagination options
 * @returns Paginated data
 * 📍 Used in:
 * - Reportes/Ventas/Necesidad Total (`total-necessity.component.ts`)
 * 
 * Search fields: detail.material.name, detail.material.code, shipTo.externalKeys.code, 
 *                shipTo.name, subRoute.name, user.user_login.username, serial
 * Array filters: type, documentClass, seller, soldTo, shipTo, materialGroupId, 
 *                warehouseId, subRouteId, materialId
 * Date filters: createdAt, deliveryDateTime, loadDateTime
 */
getOrdersGroupedByMaterial(
    options: IPagination<OrderDetailAttributes>,
    dataToFilter: IFilters,
): Observable<IPaginationResult<OrderDetailAttributes>> {
    
    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, detail.statusId!=0, y todos los filtros condicionales.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<OrderDetailAttributes>[] => {
        const deletedFilter: FilterFn<OrderDetailAttributes> = params.useOrWhere
            ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
            : where('_deleted', Ops.eq(false));   // Primer objeto del array

        const filters: FilterFn<OrderDetailAttributes>[] = [deletedFilter];

        // Filtro de estado del detalle (siempre debe aplicarse)
        filters.push(andWhere(<any>"detail.statusId", Ops.not(0)));

        // Filtro de tipo
        if (dataToFilter.type) {
            filters.push(andWhere(<any>"type.code", Ops.eq(dataToFilter.type)));
        }

        // Filtro de clase de documento
        if (dataToFilter.documentClass && dataToFilter.documentClass.length > 0) {
            filters.push(andWhere(<any>"class.code", Ops.in(...dataToFilter.documentClass)));
        }

        // Filtro de vendedor
        if (dataToFilter.seller && dataToFilter.seller.length > 0) {
            filters.push(andWhere(<any>"user.user_login.username", Ops.in(...dataToFilter.seller)));
        }

        // Filtro de soldTo
        if (dataToFilter.soldTo && dataToFilter.soldTo.length > 0) {
            filters.push(andWhere(<any>'soldTo.id', Ops.in(...dataToFilter.soldTo)));
        }

        // Filtro de shipTo (multi-select envía un arreglo; usar Ops.in en lugar de Ops.eq)
        if (dataToFilter.shipTo && dataToFilter.shipTo.length > 0) {
            filters.push(andWhere(<any>'shipTo.id', Ops.in(...dataToFilter.shipTo)));
        }

        // Filtro de oficina de ventas (multi-select del configuration-filters)
        if (dataToFilter.salesOfficeId && dataToFilter.salesOfficeId.length > 0) {
            filters.push(andWhere('salesOffice.id' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.salesOfficeId)));
        }

        // Filtro de grupo de material
        if (dataToFilter.materialGroupId && dataToFilter.materialGroupId.length > 0 && !dataToFilter.materialGroupId.includes(null)) {
            filters.push(andWhere('detail.material.materialGroups.code' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.materialGroupId)));
        }

        // Filtros de fechas (el componente envía startDate/endDate, no start/end)
        if (dataToFilter.startDate && dataToFilter.endDate) {
            filters.push(andWhere("createdAt", Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate)));
        }

        if (dataToFilter.startDeliveryDate && dataToFilter.endDeliveryDate) {
            filters.push(andWhere("deliveryDateTime" as keyof OrderDetailAttributes, Ops.btwnDates(dataToFilter.startDeliveryDate, dataToFilter.endDeliveryDate)));
        }

        if (dataToFilter.pickerUpload_start && dataToFilter.pickerUpload_end) {
            filters.push(andWhere("loadDateTime" as keyof OrderDetailAttributes, Ops.btwnDates(dataToFilter.pickerUpload_start, dataToFilter.pickerUpload_end)));
        }

        // Filtro de almacén
        if (dataToFilter.warehouseId && dataToFilter.warehouseId.length > 0) {
            filters.push(andWhere('warehouses.id' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.warehouseId)));
        }

        // Filtro de sub-ruta
        if (dataToFilter.subRouteId && dataToFilter.subRouteId.length > 0) {
            filters.push(andWhere('subRoute.id' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.subRouteId)));
        }

        // Filtro de material
        if (dataToFilter.materialId && dataToFilter.materialId.length > 0) {
            filters.push(andWhere('detail.material.code' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.materialId)));
        }

        // Filtro especial: ordersToFilter (pinea registros específicos por código de material)
        if (dataToFilter.ordersToFilter && dataToFilter.ordersToFilter.length > 0) {
            filters.push(andWhere('detail.material.code' as keyof OrderDetailAttributes, Ops.in(...dataToFilter.ordersToFilter)));
        }

        return filters;
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por MATERIAL NAME (o solo condiciones si no hay búsqueda)
    const searchByMaterialNameQueryObject: FilterFn<OrderDetailAttributes>[] = [
        ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
        hasSearch ? andWhere(<any>"detail.material.name", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por MATERIAL CODE (solo si hay búsqueda)
    const searchByMaterialCodeQueryObject: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
        ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
        andWhere(<any>'detail.material.code', Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 3: Condiciones base + búsqueda por SHIP TO EXTERNAL KEYS CODE (solo si hay búsqueda)
    const searchByShipToCodeQueryObject: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
        ...buildBaseFilters({ useOrWhere: true }),
        andWhere(<any>'shipTo.externalKeys.code', Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 4: Condiciones base + búsqueda por SHIP TO NAME (solo si hay búsqueda)
    const searchByShipToNameQueryObject: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
        ...buildBaseFilters({ useOrWhere: true }),
        andWhere(<any>'shipTo.name', Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 5: Condiciones base + búsqueda por SUB ROUTE NAME (solo si hay búsqueda)
    const searchBySubRouteNameQueryObject: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
        ...buildBaseFilters({ useOrWhere: true }),
        andWhere(<any>"subRoute.name", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 6: Condiciones base + búsqueda por USERNAME (solo si hay búsqueda)
    const searchByUsernameQueryObject: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
        ...buildBaseFilters({ useOrWhere: true }),
        andWhere(<any>"user.user_login.username", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 7: Condiciones base + búsqueda por SERIAL (solo si hay búsqueda)
    const searchBySerialQueryObject: FilterFn<OrderDetailAttributes>[] = hasSearch ? [
        ...buildBaseFilters({ useOrWhere: true }),
        andWhere(<any>"serial", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<OrderDetailAttributes>(
        // Expandimos los siete objetos en el buildQuery
        ...searchByMaterialNameQueryObject,
        ...searchByMaterialCodeQueryObject,
        ...searchByShipToCodeQueryObject,
        ...searchByShipToNameQueryObject,
        ...searchBySubRouteNameQueryObject,
        ...searchByUsernameQueryObject,
        ...searchBySerialQueryObject,

        // Ordenamiento y paginación
        orderBy(
            options.sort ?? "total",
            options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
        ),
        page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string = apiReportUrl + `api/${moduleName}/total_necessity`;
    return this.http.post<IPaginationResult<OrderDetailAttributes>>(API_URL, {
        filter: query
    });
}

    /**
    * 📍 Used in:
    * - Reportes/Ventas/Necesidad Total (`total-necessity.component.ts`)
    */
    findByNecessity(filter: CombinedFilter<OrderDetailAttributes>, options?: RequestOptions) {
        const API_URL: string =
            apiReportUrl +
            `api/${moduleName}/total_necessity`;
        const headers: Record<string, any> = {};
        // see order-customer-export for more info about this header
        // ctx: stands for context
        if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
        return this.http.post<IPaginationResult<OrderDetailAttributes>>(API_URL, 
            { filter: filter },
            { headers });
    }
}
